import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const SouthElMonteHomesForSalePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=South%20El%20Monte&county=Los%20Angeles&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>South El Monte Homes for Sale | Vietnamese Realtor | Truc Doan</title>
        <meta name="description" content="South El Monte offers attainable San Gabriel Valley homes with a close-knit, working-family community and quick access to the 60 freeway. Truc Doan is your bilingual Vietnamese realtor — call (818) 818-0989." />
        <meta name="keywords" content="South El Monte Vietnamese realtor, South El Monte homes for sale, Vietnamese realtor South El Monte, South El Monte real estate, bilingual realtor South El Monte, Vietnamese community South El Monte, multigenerational homes South El Monte, Truc Doan Vietnamese realtor" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/south-el-monte-homes-for-sale`} />
        <meta property="og:title" content="South El Monte Homes for Sale | Vietnamese Realtor | Truc Doan" />
        <meta property="og:description" content="Vietnamese buyers looking for value inside the SGV find South El Monte a practical, budget-friendly gateway to the region’s culture and amenities." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/south-el-monte-homes-for-sale`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="South El Monte, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/south-el-monte-homes-for-sale`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a bilingual Vietnamese realtor serving South El Monte, California with cultural expertise and full service in Vietnamese and English.",
            "areaServed": {
              "@type": "City",
              "name": "South El Monte, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            },
            "knowsLanguage": ["English", "Vietnamese"]
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>South El Monte Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Your trusted Vietnamese realtor for South El Monte — serving our community with bilingual expertise and cultural understanding.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Serving South El Monte's Vietnamese Community</h2>
          <p>South El Monte offers attainable San Gabriel Valley homes with a close-knit, working-family community and quick access to the 60 freeway.</p>
          <p>Vietnamese buyers looking for value inside the SGV find South El Monte a practical, budget-friendly gateway to the region’s culture and amenities.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Why Vietnamese Families Choose South El Monte</h2>
          <p>South El Monte offers what Vietnamese families value most:</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Bilingual Vietnamese Service</h3>
              <p>Full real estate service in Vietnamese and English — contracts, negotiations, and consultations in the language you are most comfortable with.</p>
            </div>
            <div className="amenity">
              <h3>Multigenerational Homes</h3>
              <p>Expertise finding homes with ADUs, in-law suites, or layouts built for extended families living together under one roof.</p>
            </div>
            <div className="amenity">
              <h3>Cultural Community</h3>
              <p>Vietnamese temples, churches, community centers, and Tết celebrations keep traditions alive for every generation.</p>
            </div>
            <div className="amenity">
              <h3>Vietnamese Schools & Tutoring</h3>
              <p>Vietnamese language schools, SAT/academic prep centers, and after-school programs serve families throughout the area.</p>
            </div>
            <div className="amenity">
              <h3>Familiar Shopping & Dining</h3>
              <p>Asian supermarkets, Vietnamese bakeries, pharmacies, and phở restaurants make daily life in South El Monte convenient and culturally connected.</p>
            </div>
            <div className="amenity">
              <h3>Strong Investment Value</h3>
              <p>Homes in this area have shown steady long-term appreciation, making them a smart purchase for families and investors alike.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>South El Monte Market Snapshot</h2>
          <p>Here is a quick look at the South El Monte market for buyers and sellers in the Vietnamese community.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>~$720,000</td></tr>
              <tr><td>Avg Days on Market</td><td>25–35 Days</td></tr>
              <tr><td>Most Active Price Range</td><td>$550K – $820K</td></tr>
              <tr><td>Popular Home Types</td><td>Single Family, Multigenerational</td></tr>
              <tr><td>Community</td><td>Strong Vietnamese cultural presence</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>South El Monte Homes for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>Listed by: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Tìm Nhà — Find Your Home with Truc Doan</h2>
          <p>Contact Truc Doan today for bilingual real estate help in South El Monte. Serving the Vietnamese community with trust and expertise.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=South El Monte Homes for Sale Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default SouthElMonteHomesForSalePage;
